<template>
	<div class="settlement-name-edit">
		<span class="settlement-name-edit__label">
			{{ translate("ui.enter_town_name") }}
		</span>
		<input
			ref="input"
			class="settlement-name-edit__input light-input"
			type="text"
			v-model="currentName"
			minlength="2"
			maxlength="20"
			@keyup.enter="handleSave"
		/>
		<label class="settlement-name-edit__description">
			{{
				translate("ui.character_limit")
					.replace("{num1}", "2")
					.replace("{num2}", "20")
			}}
		</label>
		<AppTextButton
			class="settlement-name-edit__button"
			:type="StatusTypes.PRIMARY"
			:text="translate('button.confirm')"
			:disabled="isLengthExceeded"
			@click="handleSave"
			:shadow="true"
		/>
	</div>
</template>

<script lang="ts" setup>
import AppTextButton from "@/components/AppTextButton.vue";
import { StatusTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { computed, onMounted, ref, useTemplateRef } from "vue";

const props = defineProps<{
	initialName: string;
	onSave: (newName: string) => void;
}>();

const emit = defineEmits(["closePopup"]);

const input = useTemplateRef<HTMLInputElement>("input");
const currentName = ref<string>(props.initialName);

const isLengthExceeded = computed((): boolean => {
	// The string length is calculated after whitespaces are trimmed to prevent empty space names from passing
	const trimmedLength = currentName.value.trim().length;
	return trimmedLength < 2 || trimmedLength > 20;
});

const handleSave = (): void => {
	if (isLengthExceeded.value) return;

	// The trimmed name is passed to ensure no leading or trailing whitespaces are saved to the database
	props.onSave(currentName.value.trim());
	emit("closePopup");
};

onMounted((): void => {
	requestAnimationFrame((): void => {
		if (!input.value) return;
		input.value.focus();
		input.value.select();
	});
});
</script>

<style lang="scss" scoped>
.settlement-name-edit {
	@include flex-col-center;
	padding: 0.875rem;
	width: 12.5rem;

	&__label {
		margin-bottom: 0.25rem;
		color: #372615;
		font-style: normal;
		font-weight: normal;
		font-stretch: normal;
		font-size: 0.875rem;
		line-height: normal;
		letter-spacing: normal;
		text-align: center;
	}

	&__input {
		width: 100%;
		height: 1.813rem;
	}

	&__description {
		width: 100%;
		@include flex-row-end;
		opacity: 0.8;
		font-size: 0.7rem;
	}

	&__button {
		margin-top: 0.625rem;
		width: 100%;
	}
}
</style>
